import type { MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

import { dependencyContainer } from '~/services/dependencyContainer';
import type { CalendarDayRow, CalendarEarning, CalendarWeekColumn } from '~/types/earningsCalendar.types';
import EarningsCalendarApp from '~/ui-apps/earnings-calendar-app/App';
import { buildColumnsFromRows } from '~/utilities/earningsCalendarGrid.utility';
import { getThreeWeekWindow, startOfWeekMonday } from '~/utilities/earningsCalendarDates.utility';

export type { CalendarDayRow, CalendarEarning, CalendarWeekColumn };

export const meta: MetaFunction = () => [
  { title: 'Earnings Calendar | Market Data FYI' },
];

function getTodayCalendarPosition(reference: Date): {
  todayWeekIndex: number | null;
  todayRowIndex: number | null;
} {
  const today = new Date(reference);
  today.setHours(12, 0, 0, 0);
  const day = today.getDay();
  if (day === 0 || day === 6) {
    return { todayWeekIndex: null, todayRowIndex: null };
  }

  const todayRowIndex = day - 1;
  const { lastMonday, thisMonday, nextMonday } = getThreeWeekWindow(reference);
  const mondayTime = startOfWeekMonday(today).getTime();

  let todayWeekIndex: number | null = null;
  if (mondayTime === lastMonday.getTime()) todayWeekIndex = 0;
  else if (mondayTime === thisMonday.getTime()) todayWeekIndex = 1;
  else if (mondayTime === nextMonday.getTime()) todayWeekIndex = 2;

  return { todayWeekIndex, todayRowIndex };
}

export async function loader() {
  const reference = new Date();
  const cache = await dependencyContainer.services.redisService.getEarningsCalendar();
  const rows = cache?.data ?? [];
  const symbols = [...new Set(rows.map((row) => row.symbol))];
  const tickerLinks =
    await dependencyContainer.repositories.tickerRepository.getDisplayReadyTickerLinksBySymbols(symbols);
  const linkBySymbol = new Map(
    tickerLinks.map((row: {
      symbol: string;
      ticker_id: number;
      exchange: string;
      company_id: number;
      logo_url: string | null;
      market_cap: string | number | null;
    }) => [
      row.symbol,
      {
        tickerId: row.ticker_id,
        exchange: row.exchange,
        companyId: row.company_id,
        logoUrl: row.logo_url,
        marketCap:
          row.market_cap != null && Number.isFinite(Number(row.market_cap))
            ? Number(row.market_cap)
            : null,
      },
    ]),
  );

  const enrichedRows = rows.map((row) => {
    const symbol = row.symbol.trim().toUpperCase();
    const link = linkBySymbol.get(symbol);
    if (!link) {
      return { ...row, symbol };
    }
    return {
      ...row,
      symbol,
      logoUrl: row.logoUrl ?? link.logoUrl,
      tickerId: row.tickerId ?? link.tickerId,
      exchange: row.exchange ?? link.exchange,
      companyId: row.companyId ?? link.companyId,
      marketCap: row.marketCap ?? link.marketCap,
    };
  });

  const columns = buildColumnsFromRows(reference, enrichedRows);
  const { todayWeekIndex, todayRowIndex } = getTodayCalendarPosition(reference);

  return {
    columns,
    dataUnavailable: !cache,
    todayWeekIndex,
    todayRowIndex,
  };
}

export default function EarningsCalendarRoute() {
  const data = useLoaderData<typeof loader>();
  return <EarningsCalendarApp {...data} />;
}
